import React from 'react';
import {defineMessages} from 'react-intl';
import {defineAccessControlledResource, PoliciesAccessRule, Policy} from '@igp/shared/access-control';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import {RealtimeUpdatesMode} from '@igp/shared/redux/realtime';
import type {ViewType} from '@igp/shared/redux/view';

import {withAccessCheck} from '@access-control';
import type {DebtResponsibleMasterAgentViewModelKeys} from '@features/debt-responsible-master-agent';
import {
    AddDebtResponsibleMasterAgentButton,
    DebtResponsibleMasterAgentDataGrid,
    DebtResponsibleMasterAgentQueryFilters,
    DebtResponsibleMasterAgentsDownloadCsvButton,
    useDebtResponsibleMasterAgents,
} from '@features/debt-responsible-master-agent';

const localized = defineMessages({
    title: {
        id: 'DebtResponsibleMasterAgents_title',
        defaultMessage: 'Debt Responsible Master Agents',
    },
});

function DebtResponsibleMasterAgentsInner() {
    const viewType: ViewType = 'DebtResponsibleMasterAgents';
    const keys: DebtResponsibleMasterAgentViewModelKeys[] = [
        'agent_uid',
        'username',
        'downline_agents_count',
        'aggregated_downline_debt',
        'created_by',
        'created_at',
    ];

    const {
        items,
        rawItems,
        totalCount,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        searchFilter,
        filterString,
    } = useDebtResponsibleMasterAgents({
        fields: keys,
        viewType,
        validateFilter: () => true,
        syncWithUrl: true,
        realtimeMode: RealtimeUpdatesMode.Silent,
        defaultPaging: {page: 1, pageSize: 25},
    });

    return (
        <LayoutQueue
            header={
                <PageHeader
                    header={localized.title}
                    actions={
                        <>
                            <AddDebtResponsibleMasterAgentButton />
                            <DebtResponsibleMasterAgentsDownloadCsvButton
                                keys={keys}
                                filter={filterString}
                                filename={localized.title}
                                total={totalCount}
                                mode="server"
                            />
                        </>
                    }
                />
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems primary streched>
                            <DebtResponsibleMasterAgentQueryFilters
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={['agentUid']}
                            />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <DebtResponsibleMasterAgentDataGrid
                    rows={items}
                    rawModels={rawItems}
                    paging={{
                        ...searchFilter.paging,
                        rowCount: totalCount,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onSortChange={handleSortChange}
                    sortModel={{
                        sortModel: searchFilter?.sorting,
                    }}
                    columns={[...keys, 'remove_button']}
                />
            }
        />
    );
}

export const debtResponsibleMasterAgentsResource = defineAccessControlledResource({
    name: 'DebtResponsibleMasterAgents',
    rules: new PoliciesAccessRule([new Policy('admin')]),
});

const DebtResponsibleMasterAgentsWithAccess = withAccessCheck(DebtResponsibleMasterAgentsInner, debtResponsibleMasterAgentsResource);

export const DebtResponsibleMasterAgents = withTraceErrorBoundary(DebtResponsibleMasterAgentsWithAccess, ErrorModal);
